import { Injectable, OnDestroy } from '@angular/core';
import { DownloadService } from '@literature/services/download.service';
import { LiteratureDataService } from '@literature/services/literature-data.service';
import { AppStateService, PersonalisationAPIService } from '@services';
import {
  FundId,
  LiteratureData,
  LiteratureDocType,
  LiteratureDocument,
  LiteratureDocumentData,
  PersonalisationFundDoc,
  PersonalisationFundDocs,
  PersonalisationPersonalDataDocuments,
} from '@types';
import { API_DATE_FORMAT } from '@utils/app.constants';
import { Logger } from '@utils/logger';
import { CompareFn, createCompare, SortDirection } from '@utils/sort-utils';
import dayjs from 'dayjs';
import { BehaviorSubject, Observable, of, ReplaySubject, Subject } from 'rxjs';
import { map, switchMap, takeUntil } from 'rxjs/operators';
import {
  USServicingAvailableLatestDocuments,
  USServicingLatestDocuments,
} from '../us-servicing-latest-fund-documents/types/us-servicing-latest-fund-documents.types';

const logger = Logger.getLogger('USServicingFundDocumentsService');

export interface LiteratureDocumentType {
  dctermsType: string;
  translatedDctermsType: string;
}

const documentTypesForRemovingDuplicates: LiteratureDocType[] = [
  LiteratureDocType.FUND_COMMENTARY,
  LiteratureDocType.TRADE_RATIONALE,
  LiteratureDocType.FLYER,
];

@Injectable({
  providedIn: 'root',
})
export class USServicingFundDocumentsService implements OnDestroy {
  private unsubscribe$: Subject<void> = new Subject<void>();
  private fundsWithDocs$: ReplaySubject<
    PersonalisationFundDocs[]
  > = new ReplaySubject<PersonalisationFundDocs[]>(1);
  private isDownloadCompletedSubject$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false
  );
  private numberOfDays = {
    '7Days': 7,
    '30Days': 30,
  };
  private sortByPublicationDate: CompareFn<LiteratureDocument> = createCompare<LiteratureDocument>(
    {
      dataKey: 'publicationDate',
      direction: SortDirection.DESC,
      isDate: true,
      format: API_DATE_FORMAT,
    }
  );

  constructor(
    private appStateService: AppStateService,
    private personalisationService: PersonalisationAPIService,
    private litDataService: LiteratureDataService,
    private downloadService: DownloadService
  ) {
    // listen for funds, then get associated docs
    this.personalisationService
      .getFundData$()
      .pipe(
        takeUntil(this.unsubscribe$),
        switchMap(
          (
            funds: PersonalisationFundDocs[]
          ): Observable<PersonalisationFundDocs[]> => this.getFundDocs$(funds)
        )
      )
      .subscribe((funds: PersonalisationFundDocs[]): void => {
        logger.debug('funds with docs', funds);
        this.fundsWithDocs$.next(funds);
      });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  public downloadDocsAsZip = (literatureCodes: string[]): void => {
    logger.debug('downloadDocsAsZip()', literatureCodes);
    const url = `${this.appStateService.getLiteratureDownloadZipUrl()}?channel=${this.appStateService.getChannel()}`;
    this.downloadService
      .download$(url, {
        literatureCodes,
      })
      .subscribe(
        (data) => {
          logger.debug('downloadDocsAsZip() success', data);
          if (data?.state === 'DONE') {
            logger.debug('downloadDocsAsZip() DONE');
            this.setIsDocsDownloadCompleted$(true);
          }
        },
        (err) => {
          // Handle Failure
          logger.debug('downloadDocsAsZip() error', err);
        }
      );
  };

  public getIsDocsDownloadCompleted$ = (): Observable<boolean> =>
    this.isDownloadCompletedSubject$.asObservable();

  public setIsDocsDownloadCompleted$ = (isCompleted: boolean): void =>
    this.isDownloadCompletedSubject$.next(isCompleted);

  public getFundsWithDocs$ = (): Observable<PersonalisationFundDocs[]> =>
    this.fundsWithDocs$.asObservable();

  /**
   * Returns all documents based on number of days and doc type
   * @param documents - Array of Documents
   */
  public getAllDocumentsByTabType(
    documents: PersonalisationPersonalDataDocuments[],
    tabType: string
  ): USServicingLatestDocuments {
    const sevenDaysDocs: USServicingAvailableLatestDocuments = {};
    const thirtyDaysDocs: USServicingAvailableLatestDocuments = {};
    const allLatestDocs: USServicingAvailableLatestDocuments = {};

    // TODO: this forEach look contains to many if/else statements. Should be refactored.
    documents.forEach((litDocument: PersonalisationPersonalDataDocuments) => {
      const isSevenDays = dayjs().isBefore(
        dayjs(litDocument.fileUploadDate).add(
          this.numberOfDays['7Days'],
          'day'
        ),
        'day'
      );
      const isThirtyDays = dayjs().isBefore(
        dayjs(litDocument.fileUploadDate).add(
          this.numberOfDays['30Days'],
          'day'
        ),
        'day'
      );
      if (tabType === 'By Document Type') {
        if (!(litDocument.translatedDocumentType in sevenDaysDocs)) {
          sevenDaysDocs[litDocument.translatedDocumentType] = [];
          thirtyDaysDocs[litDocument.translatedDocumentType] = [];
          allLatestDocs[litDocument.translatedDocumentType] = [];
        }
        if (isSevenDays) {
          sevenDaysDocs[litDocument.translatedDocumentType].push(litDocument);
          thirtyDaysDocs[litDocument.translatedDocumentType].push(litDocument);
          allLatestDocs[litDocument.translatedDocumentType].push(litDocument);
        } else if (!isSevenDays && isThirtyDays) {
          thirtyDaysDocs[litDocument.translatedDocumentType].push(litDocument);
          allLatestDocs[litDocument.translatedDocumentType].push(litDocument);
        } else if (!isSevenDays && !isThirtyDays) {
          allLatestDocs[litDocument.translatedDocumentType].push(litDocument);
        }
      } else {
        const productName =
          litDocument.documentType === LiteratureDocType.TRADE_RATIONALE
            ? litDocument.documentTitle
            : litDocument.productName;
        if (!(productName in sevenDaysDocs)) {
          sevenDaysDocs[productName] = [];
          thirtyDaysDocs[productName] = [];
          allLatestDocs[productName] = [];
        }
        if (isSevenDays) {
          sevenDaysDocs[productName].push(litDocument);
          thirtyDaysDocs[productName].push(litDocument);
          allLatestDocs[productName].push(litDocument);
        } else if (!isSevenDays && isThirtyDays) {
          thirtyDaysDocs[productName].push(litDocument);
          allLatestDocs[productName].push(litDocument);
        } else if (!isSevenDays && !isThirtyDays) {
          allLatestDocs[productName].push(litDocument);
        }
      }
    });
    return {
      isSevenDays: this.isObjEmpty(sevenDaysDocs),
      isThirtyDays: this.isObjEmpty(thirtyDaysDocs),
      isAllDocs: this.isObjEmpty(allLatestDocs),
      sevenDaysDocs,
      thirtyDaysDocs,
      allLatestDocs,
    };
  }

  private isObjEmpty(obj: USServicingAvailableLatestDocuments): boolean {
    return Object.values(obj).some((v) => v.length !== 0);
  }

  // get the docs from the literature API
  public getFundDocs$ = (
    funds: PersonalisationFundDocs[]
  ): Observable<PersonalisationFundDocs[]> => {
    if (funds?.length > 0) {
      // comma separated string of fund ids
      const fundIds: string[] = funds.map(
        (fund: PersonalisationFundDocs): FundId => fund.fundId
      );
      const params: Map<string, any> = new Map();
      params.set('frkOneTISNumber', [...new Set(fundIds)]);
      logger.debug('call lit data service', params);
      return this.litDataService
        .loadData(params, false, true, true)
        .pipe(
          map((data: LiteratureData) => funds.map(this.addDocsToFund(data)))
        );
    } else {
      return of([]);
    }
  };

  // process docs into fund
  private addDocsToFund = (data: LiteratureData) => (
    fund: PersonalisationFundDocs
  ): PersonalisationFundDocs => {
    const allDocs: LiteratureDocument[] = data?.document?.map(
      (item: LiteratureDocumentData): LiteratureDocument =>
        this.litDataService.literatureMapper(item)
    );
    const fundDocs: LiteratureDocument[] = allDocs
      ?.filter(
        // filter by fund id
        (doc: LiteratureDocument): boolean =>
          doc.frkOneTISNumber?.includes(fund.fundId)
      )
      .filter((doc: LiteratureDocument): boolean =>
        // filter by share class if present
        doc.frkShareClassCode?.length > 0
          ? doc.frkShareClassCode.includes(fund.shareClassCode)
          : true
      )
      .filter(
        // filter out duplicate doc types
        this.deDuplicateByType
      )
      .map(
        // augment a file link based on literature code (docDd)
        (doc: LiteratureDocument): PersonalisationFundDoc => ({
          ...doc,
          // TODO: need to fix this for intl sites
          fileLink: this.getLitDocDownloadURL(doc.customFilePath),
        })
      );

    logger.debug('addDocsToFund()', data, fund, allDocs, fundDocs);
    return { ...fund, docs: fundDocs };
  };

  /**
   * Get Lit Doc Download URL
   * @param customFilePath - string
   * @param type - string (default = 'literatureUS')
   * @returns - string
   */
  public getLitDocDownloadURL(
    customFilePath: string,
    type = 'literatureUS'
  ): string {
    return `${this.litDataService.getDownloadUrl(type)}${customFilePath}`;
  }

  /**
   * De-duplicates documents based on the document type
   * Returns true if document's type is not on the de-dupe list
   * If document is a de-dupe type, checks against the other docs of that type
   * and only returns true if this is the newest one
   */
  private deDuplicateByType = (
    doc: LiteratureDocument,
    index: number,
    filteredDocs: LiteratureDocument[]
  ): boolean => {
    if (!documentTypesForRemovingDuplicates.includes(doc.dctermsType)) {
      // return early if doc type isn't one we remove duplicates for
      return true;
    }

    // get other docs with same doc type, and sort by date
    const docsByType: LiteratureDocument[] = filteredDocs
      .filter(
        (filteredDoc: LiteratureDocument): boolean =>
          filteredDoc.dctermsType === doc.dctermsType
      )
      .sort(this.sortByPublicationDate);

    // returns true if this is the newest doc of this type
    // otherwise older docs of this type get filtered out
    return docsByType[0] === doc;
  };
}
